// @shared
// Каталог 3D-моделей воксельных аксессуаров для магазина.
// Модель — массив боксов (VoxelPart) с offset/size/color.
// usePalette берёт цвет из активной палитры персонажа (skin/hair/clothes).

export interface VoxelPart {
  x: number
  y: number
  z: number
  w: number
  h: number
  d: number
  color?: number
  usePalette?: "skin" | "hair" | "clothes"
  opacity?: number
  rotX?: number
  rotY?: number
  rotZ?: number
  /** Геометрия детали. Старые модели без поля остаются воксельными. */
  shape?: "box" | "sphere" | "cylinder" | "cone" | "torus"
  attachTo?: "outfit" | "head" | "leftArm" | "rightArm" | "leftLeg" | "rightLeg"
}

export interface AccessoryModel {
  parts: VoxelPart[]
  scale?: number
  xOffset?: number
  yOffset?: number
  zOffset?: number
  hideHair?: boolean
  coversHat?: boolean
  replacesBaseClothes?: boolean
  replacesBasePants?: boolean
  replacesBaseShoes?: boolean
  /** Правило посадки головного аксессуара относительно головы и волос. */
  headwear?: {
    mode: "cover" | "over" | "enclose"
    /** Небольшое управляемое перекрытие с волосами, в долях размера головы. */
    overlap?: number
    /** Дополнительный подъём после автоматической посадки, в долях головы. */
    lift?: number
  }
}

// ─── ПАЛИТРА ЦВЕТОВ МОДУЛЬНЫХ АКСЕССУАРОВ ───
const C = {
  red: 0xc8312b,
  redDark: 0x8b1f18,
  black: 0x111111,
  white: 0xf5f2e8,
  yellow: 0xf4d35e,
  gold: 0xe6b94a,
  gray: 0x6b7280,
  grayDark: 0x2a2f3a,
  blue: 0x3b6cd1,
  blueDark: 0x1e3a8a,
  brown: 0x8b5e3c,
  brownDark: 0x4a2e1a,
  green: 0x22c55e,
  greenDark: 0x166534,
  pink: 0xff8fa8,
  pinkDark: 0xd94a6a,
  purple: 0x8b5cf6,
  purpleDark: 0x5b21b6,
  orange: 0xff7a1f,
  cream: 0xf3e3c0,
  glass: 0xcfe8ff,
}

// Хелперы построения моделей
function plane(w: number, h: number, d: number, color: number): VoxelPart {
  return { x: 0, y: 0, z: 0, w, h, d, color }
}

// ────────────────────────────────────────────────────────────
// КАТАЛОГ
// ────────────────────────────────────────────────────────────
export const ACCESSORY_MODELS: Record<string, AccessoryModel> = {
  // ─── ШАПКИ ──────────────────────────────────────
  hat_cap_red: {
    hideHair: true,
    headwear: { mode: "cover" },
    parts: [
      // Непрерывная чаша шире головы: цилиндр закрывает виски, купол — макушку.
      { shape: "cylinder", x: 0, y: 0.13, z: 0, w: 0.94, h: 0.26, d: 0.92, color: C.red },
      { shape: "sphere", x: 0, y: 0.31, z: 0, w: 0.94, h: 0.42, d: 0.92, color: C.red },
      { x: 0, y: 0.055, z: 0.4, w: 0.78, h: 0.07, d: 0.4, color: C.redDark },
      { shape: "sphere", x: 0, y: 0.52, z: -0.03, w: 0.1, h: 0.06, d: 0.1, color: C.redDark },
    ],
  },
  hat_beanie: {
    hideHair: true,
    headwear: { mode: "cover" },
    parts: [
      { shape: "cylinder", x: 0, y: 0.11, z: 0, w: 0.96, h: 0.22, d: 0.94, color: 0x4d6481 },
      { shape: "cylinder", x: 0, y: 0.27, z: 0, w: 0.92, h: 0.28, d: 0.9, color: 0x2c3e50 },
      { shape: "sphere", x: 0, y: 0.42, z: 0, w: 0.92, h: 0.38, d: 0.9, color: 0x2c3e50 },
      { shape: "sphere", x: 0, y: 0.69, z: 0, w: 0.18, h: 0.18, d: 0.18, color: C.white },
    ],
  },
  hat_ushanka: {
    hideHair: true,
    // Длинные завязки входят в общий bounding box; компенсация сажает саму
    // тулью на голову, а завязки оставляет свободно свисать вдоль щёк.
    yOffset: -0.56,
    headwear: { mode: "cover", overlap: 0.02 },
    parts: [
      // Мягкая округлая тулья и отдельные меховые уши повторяют референс,
      // но не превращают головной убор в жёсткий куб.
      { shape: "cylinder", x: 0, y: 0.1, z: 0, w: 0.98, h: 0.2, d: 0.94, color: 0x565963 },
      { shape: "sphere", x: 0, y: 0.32, z: -0.02, w: 0.96, h: 0.48, d: 0.92, color: 0x686b74 },
      { shape: "sphere", x: 0, y: 0.27, z: 0.43, w: 0.67, h: 0.28, d: 0.13, color: 0x7a7d85 },
      { shape: "sphere", x: -0.48, y: -0.12, z: 0, w: 0.2, h: 0.58, d: 0.45, color: 0x666972, rotZ: -0.06 },
      { shape: "sphere", x: 0.48, y: -0.12, z: 0, w: 0.2, h: 0.58, d: 0.45, color: 0x666972, rotZ: 0.06 },
      { shape: "cylinder", x: -0.2, y: -0.5, z: 0.12, w: 0.035, h: 0.38, d: 0.035, color: 0x343740, rotZ: -0.12 },
      { shape: "cylinder", x: 0.2, y: -0.5, z: 0.12, w: 0.035, h: 0.38, d: 0.035, color: 0x343740, rotZ: 0.12 },
    ],
  },
  hat_party: {
    headwear: { mode: "over", overlap: 0.06 },
    parts: [
      { shape: "cone", x: 0, y: 0.27, z: 0, w: 0.48, h: 0.54, d: 0.48, color: C.pink },
      { shape: "cylinder", x: 0, y: 0.035, z: 0, w: 0.5, h: 0.07, d: 0.5, color: C.yellow },
      { shape: "sphere", x: 0, y: 0.58, z: 0, w: 0.14, h: 0.14, d: 0.14, color: C.yellow },
    ],
  },
  hat_cat_ears: {
    headwear: { mode: "over", overlap: 0.08 },
    parts: [
      { shape: "sphere", x: 0, y: 0.01, z: 0, w: 0.72, h: 0.08, d: 0.72, color: 0x222222 },
      { shape: "cone", x: -0.2, y: 0.16, z: 0.02, w: 0.22, h: 0.3, d: 0.18, color: 0x222222, rotZ: -0.12 },
      { shape: "cone", x: -0.2, y: 0.15, z: 0.105, w: 0.11, h: 0.17, d: 0.05, color: C.pink, rotZ: -0.12 },
      { shape: "cone", x: 0.2, y: 0.16, z: 0.02, w: 0.22, h: 0.3, d: 0.18, color: 0x222222, rotZ: 0.12 },
      { shape: "cone", x: 0.2, y: 0.15, z: 0.105, w: 0.11, h: 0.17, d: 0.05, color: C.pink, rotZ: 0.12 },
    ],
  },
  hat_bunny: {
    headwear: { mode: "over", overlap: 0.08 },
    parts: [
      { shape: "sphere", x: 0, y: 0.01, z: 0, w: 0.7, h: 0.09, d: 0.7, color: C.white },
      { shape: "sphere", x: -0.16, y: 0.31, z: 0, w: 0.17, h: 0.56, d: 0.13, color: C.white, rotZ: -0.08 },
      { shape: "sphere", x: -0.16, y: 0.31, z: 0.075, w: 0.08, h: 0.38, d: 0.035, color: C.pink, rotZ: -0.08 },
      { shape: "sphere", x: 0.16, y: 0.31, z: 0, w: 0.17, h: 0.56, d: 0.13, color: C.white, rotZ: 0.08 },
      { shape: "sphere", x: 0.16, y: 0.31, z: 0.075, w: 0.08, h: 0.38, d: 0.035, color: C.pink, rotZ: 0.08 },
    ],
  },
  hat_cowboy: {
    hideHair: true,
    headwear: { mode: "cover" },
    parts: [
      { shape: "cylinder", x: 0, y: 0.045, z: 0, w: 1.12, h: 0.09, d: 0.94, color: C.brown },
      { shape: "cylinder", x: 0, y: 0.22, z: 0, w: 0.94, h: 0.36, d: 0.88, color: C.brownDark },
      { shape: "sphere", x: 0, y: 0.42, z: 0, w: 0.94, h: 0.28, d: 0.88, color: C.brownDark },
      { shape: "cylinder", x: 0, y: 0.13, z: 0, w: 0.96, h: 0.08, d: 0.9, color: 0x222222 },
    ],
  },
  hat_pirate: {
    hideHair: true,
    headwear: { mode: "cover" },
    parts: [
      { shape: "cylinder", x: 0, y: 0.14, z: 0, w: 0.96, h: 0.28, d: 0.9, color: 0x111114 },
      { shape: "sphere", x: 0, y: 0.31, z: 0, w: 0.96, h: 0.38, d: 0.9, color: 0x111114 },
      { shape: "cone", x: -0.27, y: 0.38, z: 0, w: 0.44, h: 0.44, d: 0.62, color: 0x111114, rotZ: -0.4 },
      { shape: "cone", x: 0.27, y: 0.38, z: 0, w: 0.44, h: 0.44, d: 0.62, color: 0x111114, rotZ: 0.4 },
      { shape: "cylinder", x: 0, y: 0.065, z: 0, w: 1, h: 0.09, d: 0.92, color: C.redDark },
      { shape: "sphere", x: 0, y: 0.31, z: 0.46, w: 0.11, h: 0.1, d: 0.025, color: C.white },
    ],
  },
  hat_wizard: {
    hideHair: true,
    headwear: { mode: "cover" },
    parts: [
      { shape: "cylinder", x: 0, y: 0.045, z: 0, w: 1.06, h: 0.09, d: 0.94, color: C.purpleDark },
      { shape: "cylinder", x: 0, y: 0.18, z: 0, w: 0.94, h: 0.27, d: 0.88, color: C.purpleDark },
      { shape: "cone", x: 0, y: 0.54, z: 0, w: 0.82, h: 0.82, d: 0.78, color: C.purple },
      { shape: "cylinder", x: 0, y: 0.14, z: 0, w: 0.96, h: 0.09, d: 0.9, color: C.yellow },
      { shape: "sphere", x: 0.14, y: 0.55, z: 0.39, w: 0.08, h: 0.08, d: 0.035, color: C.yellow },
    ],
  },
  hat_helmet_space: {
    hideHair: true,
    headwear: { mode: "enclose" },
    parts: [
      { shape: "sphere", x: 0, y: 0, z: 0, w: 0.96, h: 0.94, d: 0.94, color: C.glass, opacity: 0.28 },
      { shape: "torus", x: 0, y: 0.34, z: 0, w: 0.88, h: 0.07, d: 0.88, color: C.white },
      { shape: "torus", x: 0, y: -0.34, z: 0, w: 0.88, h: 0.08, d: 0.88, color: C.grayDark },
      { shape: "sphere", x: -0.45, y: 0, z: 0, w: 0.13, h: 0.3, d: 0.34, color: C.white },
      { shape: "sphere", x: 0.45, y: 0, z: 0, w: 0.13, h: 0.3, d: 0.34, color: C.white },
      { x: 0.27, y: 0.48, z: -0.08, w: 0.04, h: 0.2, d: 0.04, color: C.gray },
      { shape: "sphere", x: 0.27, y: 0.61, z: -0.08, w: 0.09, h: 0.09, d: 0.09, color: C.red },
    ],
  },
  hat_devil_horns: {
    headwear: { mode: "over", overlap: 0.08 },
    parts: [
      { shape: "sphere", x: 0, y: 0.01, z: 0, w: 0.7, h: 0.07, d: 0.7, color: 0x111111 },
      { shape: "cone", x: -0.2, y: 0.2, z: 0, w: 0.19, h: 0.4, d: 0.19, color: C.red, rotZ: -0.18 },
      { shape: "cone", x: 0.2, y: 0.2, z: 0, w: 0.19, h: 0.4, d: 0.19, color: C.red, rotZ: 0.18 },
    ],
  },
  hat_crown_gold: {
    headwear: { mode: "over", overlap: 0.07 },
    parts: [
      { shape: "cylinder", x: 0, y: 0.07, z: 0, w: 0.72, h: 0.14, d: 0.72, color: C.gold },
      { shape: "cone", x: 0, y: 0.24, z: 0, w: 0.18, h: 0.34, d: 0.18, color: C.gold },
      { shape: "cone", x: -0.25, y: 0.21, z: 0, w: 0.15, h: 0.28, d: 0.15, color: C.gold, rotZ: -0.08 },
      { shape: "cone", x: 0.25, y: 0.21, z: 0, w: 0.15, h: 0.28, d: 0.15, color: C.gold, rotZ: 0.08 },
      { shape: "sphere", x: 0, y: 0.08, z: 0.37, w: 0.08, h: 0.08, d: 0.035, color: C.red },
    ],
  },
  hat_halo: {
    headwear: { mode: "over", overlap: -0.14 },
    parts: [
      { shape: "torus", x: 0, y: 0, z: 0, w: 0.74, h: 0.07, d: 0.58, color: C.yellow },
    ],
  },

  // ─── ОЧКИ И ЛИЦЕВЫЕ ─────────────────────────────
  acc_glasses_nerd: {
    parts: [
      // оправа левая
      { x: -0.16, y: 0.0, z: 0, w: 0.16, h: 0.16, d: 0.04, color: 0x1f1f1f },
      // линза левая
      { x: -0.16, y: 0.0, z: 0.015, w: 0.13, h: 0.13, d: 0.01, color: C.glass, opacity: 0.4 },
      // оправа правая
      { x: 0.16, y: 0.0, z: 0, w: 0.16, h: 0.16, d: 0.04, color: 0x1f1f1f },
      { x: 0.16, y: 0.0, z: 0.015, w: 0.13, h: 0.13, d: 0.01, color: C.glass, opacity: 0.4 },
      // мостик
      { x: 0, y: 0.0, z: 0, w: 0.08, h: 0.03, d: 0.03, color: 0x1f1f1f },
    ],
  },
  acc_shades: {
    parts: [
      { x: -0.16, y: 0.0, z: 0, w: 0.18, h: 0.1, d: 0.04, color: 0x111111 },
      { x: 0.16, y: 0.0, z: 0, w: 0.18, h: 0.1, d: 0.04, color: 0x111111 },
      { x: 0, y: 0.0, z: 0, w: 0.08, h: 0.025, d: 0.03, color: 0x111111 },
      // блики
      { x: -0.2, y: 0.02, z: 0.025, w: 0.04, h: 0.03, d: 0.005, color: C.glass, opacity: 0.6 },
      { x: 0.12, y: 0.02, z: 0.025, w: 0.04, h: 0.03, d: 0.005, color: C.glass, opacity: 0.6 },
    ],
  },
  acc_mask_ninja: {
    // face anchor — у переда лица; смещаем модель назад, чтобы она
    // обнимала голову, а не висела горизонтально перед ней
    zOffset: -0.27,
    parts: [
      // основная маска на нижнюю часть лица (нос/рот)
      { x: 0, y: -0.04, z: 0.27, w: 0.5, h: 0.22, d: 0.04, color: 0x111114 },
      // верхняя кромка
      { x: 0, y: 0.08, z: 0.27, w: 0.5, h: 0.04, d: 0.04, color: C.red },
      // боковые тесёмки оборачивают голову
      { x: -0.27, y: -0.04, z: 0.05, w: 0.06, h: 0.18, d: 0.46, color: 0x111114 },
      { x: 0.27, y: -0.04, z: 0.05, w: 0.06, h: 0.18, d: 0.46, color: 0x111114 },
      // узел сзади
      { x: 0, y: -0.04, z: -0.22, w: 0.18, h: 0.18, d: 0.06, color: 0x111114 },
      // свисающие концы повязки
      { x: -0.06, y: -0.18, z: -0.24, w: 0.05, h: 0.22, d: 0.04, color: 0x111114, rotZ: -0.2 },
      { x: 0.06, y: -0.18, z: -0.24, w: 0.05, h: 0.22, d: 0.04, color: 0x111114, rotZ: 0.2 },
    ],
  },
  acc_headphones: {
    // face anchor — впереди лица; смещаем назад к центру головы
    zOffset: -0.28,
    yOffset: 0.04,
    parts: [
      // Мягкая заметная дуга и боковые стойки проходят снаружи головы.
      { shape: "sphere", x: 0, y: 0.58, z: 0.1, w: 0.78, h: 0.1, d: 0.62, color: C.purpleDark },
      { shape: "sphere", x: -0.42, y: 0.29, z: 0.06, w: 0.08, h: 0.54, d: 0.12, color: C.purpleDark },
      { shape: "sphere", x: 0.42, y: 0.29, z: 0.06, w: 0.08, h: 0.54, d: 0.12, color: C.purpleDark },
      // Чашки вынесены за визуальную ширину самой большой головы.
      { shape: "sphere", x: -0.46, y: 0.0, z: 0, w: 0.16, h: 0.28, d: 0.25, color: C.purple },
      { shape: "sphere", x: -0.475, y: 0.0, z: 0.01, w: 0.08, h: 0.2, d: 0.18, color: C.yellow },
      { shape: "sphere", x: 0.46, y: 0.0, z: 0, w: 0.16, h: 0.28, d: 0.25, color: C.purple },
      { shape: "sphere", x: 0.475, y: 0.0, z: 0.01, w: 0.08, h: 0.2, d: 0.18, color: C.yellow },
      // микрофон сбоку
      { x: 0.46, y: -0.07, z: 0.2, w: 0.045, h: 0.045, d: 0.2, color: C.grayDark },
      { shape: "sphere", x: 0.46, y: -0.09, z: 0.32, w: 0.075, h: 0.075, d: 0.06, color: C.yellow },
    ],
  },
  acc_nose_ring: {
    // Отдельный piercing-слот позволяет носить кольцо вместе с очками.
    // Симметричное кольцо септума: центр совпадает с центром носа, а плоскость
    // торуса развёрнута к камере, поэтому отверстие читается круглым.
    xOffset: 0,
    yOffset: -0.105,
    zOffset: 0.045,
    parts: [
      { shape: "torus", x: 0, y: 0, z: 0, w: 0.075, h: 0.016, d: 0.075, color: 0xd0d3da, rotX: -Math.PI / 2 },
    ],
  },

  // ─── ШЕЯ ────────────────────────────────────────
  acc_scarf: {
    parts: [
      // Кольцо лежит вокруг шеи и оставляет свободными лицо и плечи.
      { shape: "torus", x: 0, y: -0.12, z: 0, w: 0.74, h: 0.1, d: 0.58, color: C.red },
      // Тёмная полоса лежит перед основной тканью, а не внутри неё: две
      // пересекающиеся коробки раньше мерцали при повороте персонажа.
      { x: 0, y: -0.105, z: 0.32, w: 0.62, h: 0.026, d: 0.025, color: C.redDark },
      // свисающий конец
      { x: 0.16, y: -0.3, z: 0.34, w: 0.12, h: 0.32, d: 0.07, color: C.red },
      { x: 0.16, y: -0.505, z: 0.38, w: 0.14, h: 0.035, d: 0.018, color: C.redDark },
    ],
  },
  acc_medal: {
    // The neck anchor is central; move the whole medal in front of every
    // clothing depth so the ribbon and medallion cannot disappear in it.
    yOffset: -0.32,
    zOffset: 0.18,
    parts: [
      // лента
      { x: -0.12, y: 0.04, z: 0.12, w: 0.04, h: 0.18, d: 0.02, color: C.blue },
      { x: 0.12, y: 0.04, z: 0.12, w: 0.04, h: 0.18, d: 0.02, color: C.blue },
      // медаль
      { shape: "cylinder", x: 0, y: -0.16, z: 0.18, w: 0.22, h: 0.06, d: 0.22, color: C.gold, rotX: Math.PI / 2 },
      { shape: "cylinder", x: 0, y: -0.16, z: 0.215, w: 0.15, h: 0.018, d: 0.15, color: C.brownDark, rotX: Math.PI / 2 },
      // звёздочка
      { shape: "sphere", x: 0, y: -0.16, z: 0.235, w: 0.065, h: 0.065, d: 0.018, color: C.yellow },
    ],
  },

  // ─── СПИНА ──────────────────────────────────────
  acc_backpack: {
    parts: [
      // основной мешок
      { x: 0, y: 0.05, z: -0.12, w: 0.5, h: 0.6, d: 0.22, color: 0x2a4d8a },
      // кармашек
      { x: 0, y: -0.1, z: -0.24, w: 0.34, h: 0.22, d: 0.04, color: 0x1e3a6e },
      // лямки
      { x: -0.24, y: 0.18, z: 0.0, w: 0.06, h: 0.42, d: 0.06, color: 0x1e3a6e },
      { x: 0.24, y: 0.18, z: 0.0, w: 0.06, h: 0.42, d: 0.06, color: 0x1e3a6e },
      // верхний клапан
      { x: 0, y: 0.36, z: -0.12, w: 0.52, h: 0.06, d: 0.24, color: 0x1e3a6e },
    ],
  },
  acc_wings: {
    parts: [
      // левое крыло (3 пера)
      { x: -0.4, y: 0.2, z: -0.1, w: 0.3, h: 0.28, d: 0.04, color: C.white, rotZ: 0.3 },
      { x: -0.5, y: 0.05, z: -0.1, w: 0.32, h: 0.22, d: 0.04, color: C.white, rotZ: 0.4 },
      { x: -0.55, y: -0.12, z: -0.1, w: 0.28, h: 0.18, d: 0.04, color: C.white, rotZ: 0.5 },
      // правое крыло
      { x: 0.4, y: 0.2, z: -0.1, w: 0.3, h: 0.28, d: 0.04, color: C.white, rotZ: -0.3 },
      { x: 0.5, y: 0.05, z: -0.1, w: 0.32, h: 0.22, d: 0.04, color: C.white, rotZ: -0.4 },
      { x: 0.55, y: -0.12, z: -0.1, w: 0.28, h: 0.18, d: 0.04, color: C.white, rotZ: -0.5 },
    ],
  },

  // ─── НАРЯДЫ (дополнительный декор поверх туловища) ──
  // ВАЖНО: outfit anchor — центр туловища (bodyY).
  // Тело ~ 0.5 × 0.7 × 0.3, поэтому полноценная одежда строится как
  // оболочка вокруг тела + длинная юбка/штанины ниже пояса.
  outfit_lab: {
    replacesBaseClothes: true,
    parts: [
      // основа халата (немного шире тела)
      { x: 0, y: 0, z: 0, w: 0.62, h: 0.74, d: 0.38, color: C.white },
      // длинные полы халата ниже пояса
      { x: 0, y: -0.5, z: 0, w: 0.66, h: 0.32, d: 0.4, color: C.white },
      // воротник-стойка
      { x: 0, y: 0.36, z: 0.16, w: 0.34, h: 0.08, d: 0.06, color: 0xe6e6ee },
      // лацканы (V-образный вырез)
      { x: -0.12, y: 0.18, z: 0.2, w: 0.14, h: 0.34, d: 0.04, color: 0xe6e6ee, rotZ: -0.18 },
      { x: 0.12, y: 0.18, z: 0.2, w: 0.14, h: 0.34, d: 0.04, color: 0xe6e6ee, rotZ: 0.18 },
      // нагрудный карман
      { x: 0.16, y: 0.06, z: 0.22, w: 0.14, h: 0.14, d: 0.02, color: 0xeaeaf2 },
      // ручка
      { x: 0.16, y: 0.12, z: 0.23, w: 0.02, h: 0.1, d: 0.02, color: C.green },
      // нижний карман
      { x: -0.18, y: -0.16, z: 0.22, w: 0.16, h: 0.14, d: 0.02, color: 0xeaeaf2 },
      // пуговицы по центру
      { x: 0, y: 0.02, z: 0.22, w: 0.025, h: 0.025, d: 0.015, color: 0x9aa0a6 },
      { x: 0, y: -0.12, z: 0.22, w: 0.025, h: 0.025, d: 0.015, color: 0x9aa0a6 },
      { x: 0, y: -0.26, z: 0.22, w: 0.025, h: 0.025, d: 0.015, color: 0x9aa0a6 },
      // длинные рукава следуют за руками; кисти остаются открытыми
      { attachTo: "leftArm", x: 0, y: 0.06, z: 0, w: 0.25, h: 0.46, d: 0.24, color: C.white },
      { attachTo: "rightArm", x: 0, y: 0.06, z: 0, w: 0.25, h: 0.46, d: 0.24, color: C.white },
      { attachTo: "leftArm", x: 0, y: -0.18, z: 0, w: 0.26, h: 0.06, d: 0.25, color: 0xe6e6ee },
      { attachTo: "rightArm", x: 0, y: -0.18, z: 0, w: 0.26, h: 0.06, d: 0.25, color: 0xe6e6ee },
    ],
  },
  outfit_tracksuit: {
    replacesBaseClothes: true,
    replacesBasePants: true,
    parts: [
      // Цельная олимпийка полностью заменяет базовый верх, а не лежит на нём топом.
      { x: 0, y: 0, z: 0, w: 0.68, h: 0.82, d: 0.42, color: C.blueDark },
      { x: 0, y: 0.31, z: -0.08, w: 0.54, h: 0.2, d: 0.32, color: C.blueDark },
      { x: 0, y: 0.34, z: 0.235, w: 0.32, h: 0.1, d: 0.05, color: 0x152558 },
      { x: 0, y: -0.35, z: 0, w: 0.68, h: 0.08, d: 0.42, color: 0x152558 },
      // Рукава и штанины прикреплены к конечностям и двигаются вместе с ними.
      { attachTo: "leftArm", x: 0, y: 0.07, z: 0, w: 0.26, h: 0.46, d: 0.25, color: C.blueDark },
      { attachTo: "rightArm", x: 0, y: 0.07, z: 0, w: 0.26, h: 0.46, d: 0.25, color: C.blueDark },
      { attachTo: "leftLeg", x: 0, y: 0, z: 0, w: 0.3, h: 0.68, d: 0.27, color: C.blueDark },
      { attachTo: "rightLeg", x: 0, y: 0, z: 0, w: 0.3, h: 0.68, d: 0.27, color: C.blueDark },
      { attachTo: "leftLeg", x: 0, y: -0.29, z: 0, w: 0.31, h: 0.07, d: 0.29, color: 0x152558 },
      { attachTo: "rightLeg", x: 0, y: -0.29, z: 0, w: 0.31, h: 0.07, d: 0.29, color: 0x152558 },
      // Белые полосы вынесены за поверхность ткани, чтобы не мерцать.
      { attachTo: "leftArm", x: -0.141, y: 0.07, z: 0.07, w: 0.018, h: 0.38, d: 0.035, color: C.white },
      { attachTo: "leftArm", x: -0.141, y: 0.07, z: 0, w: 0.018, h: 0.38, d: 0.035, color: C.white },
      { attachTo: "leftArm", x: -0.141, y: 0.07, z: -0.07, w: 0.018, h: 0.38, d: 0.035, color: C.white },
      { attachTo: "rightArm", x: 0.141, y: 0.07, z: 0.07, w: 0.018, h: 0.38, d: 0.035, color: C.white },
      { attachTo: "rightArm", x: 0.141, y: 0.07, z: 0, w: 0.018, h: 0.38, d: 0.035, color: C.white },
      { attachTo: "rightArm", x: 0.141, y: 0.07, z: -0.07, w: 0.018, h: 0.38, d: 0.035, color: C.white },
      { attachTo: "leftLeg", x: -0.161, y: 0, z: 0, w: 0.018, h: 0.58, d: 0.05, color: C.white },
      { attachTo: "rightLeg", x: 0.161, y: 0, z: 0, w: 0.018, h: 0.58, d: 0.05, color: C.white },
      { x: 0, y: 0.03, z: 0.226, w: 0.022, h: 0.62, d: 0.02, color: 0xc0c4cc },
      { x: -0.18, y: 0.18, z: 0.227, w: 0.12, h: 0.07, d: 0.022, color: C.white },
    ],
  },
  outfit_suit: {
    replacesBaseClothes: true,
    replacesBasePants: true,
    parts: [
      { x: 0, y: 0, z: 0, w: 0.68, h: 0.82, d: 0.42, color: 0x1a1a26 },
      { x: 0, y: -0.35, z: 0, w: 0.69, h: 0.1, d: 0.43, color: 0x080810 },
      { x: 0, y: -0.35, z: 0.231, w: 0.07, h: 0.06, d: 0.022, color: C.gold },
      { attachTo: "leftArm", x: 0, y: 0.07, z: 0, w: 0.26, h: 0.46, d: 0.25, color: 0x1a1a26 },
      { attachTo: "rightArm", x: 0, y: 0.07, z: 0, w: 0.26, h: 0.46, d: 0.25, color: 0x1a1a26 },
      { attachTo: "leftLeg", x: 0, y: 0, z: 0, w: 0.3, h: 0.68, d: 0.27, color: 0x141422 },
      { attachTo: "rightLeg", x: 0, y: 0, z: 0, w: 0.3, h: 0.68, d: 0.27, color: 0x141422 },
      { attachTo: "leftLeg", x: 0, y: -0.29, z: 0.02, w: 0.31, h: 0.07, d: 0.3, color: 0x080810 },
      { attachTo: "rightLeg", x: 0, y: -0.29, z: 0.02, w: 0.31, h: 0.07, d: 0.3, color: 0x080810 },
      // воротник-стойка белой рубашки
      { x: 0, y: 0.34, z: 0.23, w: 0.3, h: 0.09, d: 0.04, color: C.white },
      { x: 0, y: 0.07, z: 0.232, w: 0.2, h: 0.46, d: 0.025, color: C.white },
      // лацканы
      { x: -0.16, y: 0.16, z: 0.248, w: 0.18, h: 0.46, d: 0.025, color: 0x0c0c14, rotZ: -0.18 },
      { x: 0.16, y: 0.16, z: 0.248, w: 0.18, h: 0.46, d: 0.025, color: 0x0c0c14, rotZ: 0.18 },
      // галстук — узел
      { x: 0, y: 0.3, z: 0.267, w: 0.07, h: 0.08, d: 0.025, color: C.red },
      { x: 0, y: 0.06, z: 0.267, w: 0.08, h: 0.4, d: 0.025, color: C.red },
      { x: 0, y: -0.16, z: 0.267, w: 0.1, h: 0.06, d: 0.025, color: C.redDark },
      { x: 0.19, y: 0.22, z: 0.266, w: 0.09, h: 0.06, d: 0.02, color: C.white },
      { x: 0, y: -0.06, z: 0.267, w: 0.03, h: 0.03, d: 0.02, color: C.gold },
      { x: 0, y: -0.18, z: 0.267, w: 0.03, h: 0.03, d: 0.02, color: C.gold },
    ],
  },
  outfit_dragon: {
    hideHair: true,
    coversHat: true,
    replacesBaseClothes: true,
    replacesBasePants: true,
    parts: [
      // Цельный комбинезон полностью заменяет базовую одежду.
      { x: 0, y: 0, z: 0, w: 0.68, h: 0.82, d: 0.42, color: C.green },
      { attachTo: "leftArm", x: 0, y: 0.07, z: 0, w: 0.26, h: 0.46, d: 0.25, color: C.green },
      { attachTo: "rightArm", x: 0, y: 0.07, z: 0, w: 0.26, h: 0.46, d: 0.25, color: C.green },
      { attachTo: "leftLeg", x: 0, y: 0, z: 0, w: 0.3, h: 0.68, d: 0.27, color: C.green },
      { attachTo: "rightLeg", x: 0, y: 0, z: 0, w: 0.3, h: 0.68, d: 0.27, color: C.green },
      // светлый живот
      { x: 0, y: -0.04, z: 0.236, w: 0.38, h: 0.54, d: 0.03, color: 0x76d180 },
      { x: 0, y: 0.22, z: 0.257, w: 0.32, h: 0.045, d: 0.012, color: C.greenDark },
      { x: 0, y: 0.1, z: 0.257, w: 0.32, h: 0.045, d: 0.012, color: C.greenDark },
      { x: 0, y: -0.02, z: 0.257, w: 0.32, h: 0.045, d: 0.012, color: C.greenDark },
      { x: 0, y: -0.14, z: 0.257, w: 0.32, h: 0.045, d: 0.012, color: C.greenDark },
      // плечевые накладки-шипы
      { x: -0.35, y: 0.32, z: 0, w: 0.12, h: 0.12, d: 0.34, color: C.greenDark },
      { x: 0.35, y: 0.32, z: 0, w: 0.12, h: 0.12, d: 0.34, color: C.greenDark },
      // шипы вдоль спины
      { x: 0, y: 0.32, z: -0.255, w: 0.07, h: 0.16, d: 0.08, color: C.greenDark },
      { x: 0, y: 0.12, z: -0.255, w: 0.07, h: 0.18, d: 0.08, color: C.greenDark },
      { x: 0, y: -0.08, z: -0.255, w: 0.07, h: 0.16, d: 0.08, color: C.greenDark },
      { x: 0, y: -0.28, z: -0.255, w: 0.06, h: 0.14, d: 0.08, color: C.greenDark },
      // Округлый капюшон обнимает голову сзади и по бокам, оставляя лицо открытым.
      { shape: "sphere", attachTo: "head", x: 0, y: 0.3, z: -0.02, w: 0.74, h: 0.2, d: 0.72, color: C.green },
      { shape: "sphere", attachTo: "head", x: -0.34, y: 0, z: -0.03, w: 0.13, h: 0.58, d: 0.58, color: C.green },
      { shape: "sphere", attachTo: "head", x: 0.34, y: 0, z: -0.03, w: 0.13, h: 0.58, d: 0.58, color: C.green },
      { shape: "sphere", attachTo: "head", x: 0, y: 0, z: -0.34, w: 0.62, h: 0.58, d: 0.13, color: C.greenDark },
      { shape: "sphere", attachTo: "head", x: 0, y: 0.22, z: 0.31, w: 0.56, h: 0.1, d: 0.09, color: C.green },
      { shape: "cone", attachTo: "head", x: -0.18, y: 0.43, z: -0.03, w: 0.11, h: 0.2, d: 0.11, color: C.yellow, rotZ: -0.22 },
      { shape: "cone", attachTo: "head", x: 0.18, y: 0.43, z: -0.03, w: 0.11, h: 0.2, d: 0.11, color: C.yellow, rotZ: 0.22 },
      // длинный хвост из звеньев
      { x: 0, y: -0.42, z: -0.28, w: 0.12, h: 0.12, d: 0.2, color: C.green },
      { x: 0, y: -0.5, z: -0.44, w: 0.09, h: 0.09, d: 0.16, color: C.green },
      { x: 0, y: -0.56, z: -0.56, w: 0.07, h: 0.07, d: 0.12, color: C.greenDark },
      // когти/пуговицы спереди
      { x: -0.1, y: 0.27, z: 0.263, w: 0.035, h: 0.035, d: 0.018, color: C.yellow },
      { x: 0.1, y: 0.27, z: 0.263, w: 0.035, h: 0.035, d: 0.018, color: C.yellow },
    ],
  },

  // ─── ОТДЕЛЬНЫЕ ЧАСТИ ОБРАЗА ─────────────────────────
  outerwear_leather_jacket: {
    replacesBaseClothes: true,
    parts: [
      { x: 0, y: 0, z: 0, w: 0.68, h: 0.82, d: 0.42, color: 0x15161a },
      { x: 0, y: 0.08, z: 0.225, w: 0.2, h: 0.56, d: 0.04, color: 0x292b33 },
      { x: -0.17, y: 0.18, z: 0.25, w: 0.17, h: 0.42, d: 0.035, color: 0x08090b, rotZ: -0.2 },
      { x: 0.17, y: 0.18, z: 0.25, w: 0.17, h: 0.42, d: 0.035, color: 0x08090b, rotZ: 0.2 },
      { x: 0, y: -0.02, z: 0.273, w: 0.022, h: 0.58, d: 0.018, color: 0xc1c4ca },
      { x: -0.22, y: -0.12, z: 0.265, w: 0.13, h: 0.025, d: 0.018, color: 0xc1c4ca, rotZ: 0.16 },
      { x: 0.22, y: -0.12, z: 0.265, w: 0.13, h: 0.025, d: 0.018, color: 0xc1c4ca, rotZ: -0.16 },
      { attachTo: "leftArm", x: 0, y: 0.06, z: 0, w: 0.27, h: 0.47, d: 0.26, color: 0x15161a },
      { attachTo: "rightArm", x: 0, y: 0.06, z: 0, w: 0.27, h: 0.47, d: 0.26, color: 0x15161a },
      { attachTo: "leftArm", x: 0, y: -0.19, z: 0, w: 0.28, h: 0.055, d: 0.27, color: 0x08090b },
      { attachTo: "rightArm", x: 0, y: -0.19, z: 0, w: 0.28, h: 0.055, d: 0.27, color: 0x08090b },
    ],
  },
  bottom_black_jeans: {
    replacesBasePants: true,
    parts: [
      { attachTo: "leftLeg", x: 0, y: 0, z: 0, w: 0.3, h: 0.68, d: 0.27, color: 0x17191f },
      { attachTo: "rightLeg", x: 0, y: 0, z: 0, w: 0.3, h: 0.68, d: 0.27, color: 0x17191f },
      { attachTo: "leftLeg", x: 0.13, y: 0, z: 0.14, w: 0.018, h: 0.56, d: 0.018, color: 0x343741 },
      { attachTo: "rightLeg", x: -0.13, y: 0, z: 0.14, w: 0.018, h: 0.56, d: 0.018, color: 0x343741 },
      { attachTo: "leftLeg", x: 0, y: 0.29, z: 0.145, w: 0.24, h: 0.035, d: 0.018, color: 0x3d4049 },
      { attachTo: "rightLeg", x: 0, y: 0.29, z: 0.145, w: 0.24, h: 0.035, d: 0.018, color: 0x3d4049 },
    ],
  },
  shoes_combat_boots: {
    replacesBaseShoes: true,
    parts: [
      { attachTo: "leftLeg", x: 0, y: -0.18, z: 0.025, w: 0.32, h: 0.38, d: 0.32, color: 0x0d0e11 },
      { attachTo: "rightLeg", x: 0, y: -0.18, z: 0.025, w: 0.32, h: 0.38, d: 0.32, color: 0x0d0e11 },
      { attachTo: "leftLeg", x: 0, y: -0.36, z: 0.055, w: 0.34, h: 0.07, d: 0.38, color: 0x050608 },
      { attachTo: "rightLeg", x: 0, y: -0.36, z: 0.055, w: 0.34, h: 0.07, d: 0.38, color: 0x050608 },
      { attachTo: "leftLeg", x: 0, y: -0.17, z: 0.19, w: 0.12, h: 0.26, d: 0.025, color: 0x353842 },
      { attachTo: "rightLeg", x: 0, y: -0.17, z: 0.19, w: 0.12, h: 0.26, d: 0.025, color: 0x353842 },
    ],
  },

  // ─── ПИТОМЦЫ ────────────────────────────────────
  pet_cat: {
    yOffset: 0.0,
    parts: [
      // тело
      { x: 0, y: 0.16, z: 0, w: 0.26, h: 0.18, d: 0.4, color: 0xf1c282 },
      // голова
      { x: 0, y: 0.34, z: 0.18, w: 0.22, h: 0.2, d: 0.2, color: 0xf1c282 },
      // уши
      { x: -0.08, y: 0.46, z: 0.16, w: 0.06, h: 0.08, d: 0.06, color: 0xf1c282 },
      { x: 0.08, y: 0.46, z: 0.16, w: 0.06, h: 0.08, d: 0.06, color: 0xf1c282 },
      // глаза
      { x: -0.06, y: 0.36, z: 0.28, w: 0.03, h: 0.03, d: 0.02, color: 0x111111 },
      { x: 0.06, y: 0.36, z: 0.28, w: 0.03, h: 0.03, d: 0.02, color: 0x111111 },
      // нос
      { x: 0, y: 0.32, z: 0.28, w: 0.03, h: 0.02, d: 0.02, color: C.pinkDark },
      // лапы
      { x: -0.1, y: 0.04, z: 0.16, w: 0.07, h: 0.1, d: 0.08, color: 0xc99356 },
      { x: 0.1, y: 0.04, z: 0.16, w: 0.07, h: 0.1, d: 0.08, color: 0xc99356 },
      { x: -0.1, y: 0.04, z: -0.14, w: 0.07, h: 0.1, d: 0.08, color: 0xc99356 },
      { x: 0.1, y: 0.04, z: -0.14, w: 0.07, h: 0.1, d: 0.08, color: 0xc99356 },
      // хвост
      { x: 0, y: 0.28, z: -0.24, w: 0.06, h: 0.06, d: 0.2, color: 0xf1c282, rotX: -0.4 },
    ],
  },
  pet_puppy: {
    yOffset: 0.0,
    parts: [
      // тело
      { x: 0, y: 0.18, z: 0, w: 0.3, h: 0.2, d: 0.42, color: C.brown },
      // голова
      { x: 0, y: 0.32, z: 0.22, w: 0.26, h: 0.22, d: 0.22, color: C.brown },
      // уши вислые
      { x: -0.14, y: 0.34, z: 0.18, w: 0.06, h: 0.16, d: 0.1, color: C.brownDark },
      { x: 0.14, y: 0.34, z: 0.18, w: 0.06, h: 0.16, d: 0.1, color: C.brownDark },
      // морда
      { x: 0, y: 0.28, z: 0.32, w: 0.14, h: 0.1, d: 0.08, color: 0xf3e3c0 },
      // глаза
      { x: -0.06, y: 0.36, z: 0.32, w: 0.03, h: 0.03, d: 0.02, color: 0x111111 },
      { x: 0.06, y: 0.36, z: 0.32, w: 0.03, h: 0.03, d: 0.02, color: 0x111111 },
      // нос
      { x: 0, y: 0.3, z: 0.36, w: 0.04, h: 0.03, d: 0.02, color: 0x111111 },
      // лапы
      { x: -0.1, y: 0.05, z: 0.16, w: 0.08, h: 0.12, d: 0.1, color: C.brownDark },
      { x: 0.1, y: 0.05, z: 0.16, w: 0.08, h: 0.12, d: 0.1, color: C.brownDark },
      { x: -0.1, y: 0.05, z: -0.16, w: 0.08, h: 0.12, d: 0.1, color: C.brownDark },
      { x: 0.1, y: 0.05, z: -0.16, w: 0.08, h: 0.12, d: 0.1, color: C.brownDark },
      // хвост
      { x: 0, y: 0.3, z: -0.26, w: 0.06, h: 0.14, d: 0.06, color: C.brown },
    ],
  },
  pet_owl: {
    yOffset: 0.1,
    parts: [
      // тело-яйцо
      { x: 0, y: 0.2, z: 0, w: 0.3, h: 0.4, d: 0.26, color: 0xb98a4d },
      // живот
      { x: 0, y: 0.18, z: 0.12, w: 0.2, h: 0.3, d: 0.04, color: C.cream },
      // голова
      { x: 0, y: 0.5, z: 0, w: 0.32, h: 0.22, d: 0.26, color: 0xb98a4d },
      // глаза диски
      { x: -0.07, y: 0.5, z: 0.13, w: 0.1, h: 0.12, d: 0.02, color: C.white },
      { x: 0.07, y: 0.5, z: 0.13, w: 0.1, h: 0.12, d: 0.02, color: C.white },
      { x: -0.07, y: 0.5, z: 0.14, w: 0.05, h: 0.06, d: 0.01, color: 0x111111 },
      { x: 0.07, y: 0.5, z: 0.14, w: 0.05, h: 0.06, d: 0.01, color: 0x111111 },
      // клюв
      { x: 0, y: 0.44, z: 0.14, w: 0.05, h: 0.06, d: 0.04, color: C.yellow },
      // ушки
      { x: -0.13, y: 0.66, z: 0, w: 0.06, h: 0.08, d: 0.06, color: 0xb98a4d },
      { x: 0.13, y: 0.66, z: 0, w: 0.06, h: 0.08, d: 0.06, color: 0xb98a4d },
      // крылья
      { x: -0.18, y: 0.22, z: 0, w: 0.06, h: 0.28, d: 0.18, color: 0x886030 },
      { x: 0.18, y: 0.22, z: 0, w: 0.06, h: 0.28, d: 0.18, color: 0x886030 },
      // лапы
      { x: -0.06, y: 0.0, z: 0.06, w: 0.04, h: 0.04, d: 0.06, color: C.yellow },
      { x: 0.06, y: 0.0, z: 0.06, w: 0.04, h: 0.04, d: 0.06, color: C.yellow },
    ],
  },
  pet_dragon: {
    yOffset: 0.1,
    parts: [
      // тело
      { x: 0, y: 0.2, z: 0, w: 0.32, h: 0.24, d: 0.4, color: 0x2c8a3a },
      // живот светлее
      { x: 0, y: 0.16, z: 0.12, w: 0.22, h: 0.18, d: 0.04, color: 0x76d180 },
      // голова
      { x: 0, y: 0.36, z: 0.22, w: 0.28, h: 0.22, d: 0.24, color: 0x2c8a3a },
      // морда
      { x: 0, y: 0.32, z: 0.36, w: 0.18, h: 0.14, d: 0.08, color: 0x238030 },
      // глаза
      { x: -0.07, y: 0.4, z: 0.32, w: 0.04, h: 0.04, d: 0.02, color: C.yellow },
      { x: 0.07, y: 0.4, z: 0.32, w: 0.04, h: 0.04, d: 0.02, color: C.yellow },
      // рожки
      { x: -0.08, y: 0.5, z: 0.16, w: 0.05, h: 0.1, d: 0.05, color: C.cream },
      { x: 0.08, y: 0.5, z: 0.16, w: 0.05, h: 0.1, d: 0.05, color: C.cream },
      // крылья
      { x: -0.2, y: 0.32, z: -0.04, w: 0.06, h: 0.26, d: 0.22, color: 0x76d180, rotZ: 0.3 },
      { x: 0.2, y: 0.32, z: -0.04, w: 0.06, h: 0.26, d: 0.22, color: 0x76d180, rotZ: -0.3 },
      // шипы
      { x: 0, y: 0.36, z: -0.12, w: 0.04, h: 0.08, d: 0.04, color: 0x76d180 },
      { x: 0, y: 0.34, z: -0.22, w: 0.04, h: 0.08, d: 0.04, color: 0x76d180 },
      // лапы
      { x: -0.1, y: 0.05, z: 0.14, w: 0.08, h: 0.1, d: 0.1, color: 0x238030 },
      { x: 0.1, y: 0.05, z: 0.14, w: 0.08, h: 0.1, d: 0.1, color: 0x238030 },
      { x: -0.1, y: 0.05, z: -0.14, w: 0.08, h: 0.1, d: 0.1, color: 0x238030 },
      { x: 0.1, y: 0.05, z: -0.14, w: 0.08, h: 0.1, d: 0.1, color: 0x238030 },
      // хвост
      { x: 0, y: 0.18, z: -0.3, w: 0.08, h: 0.08, d: 0.16, color: 0x2c8a3a },
    ],
  },
}

export function getAccessoryModel(itemId: string): AccessoryModel | null {
  return ACCESSORY_MODELS[itemId] ?? null
}
